import { useMemo } from 'react';
import dynamic from 'next/dynamic';
import { BodyTextSmall } from 'lib/design-system';
import {
  RAQBannerWrapper,
  MobileView,
  DesktopView,
  FauxButton,
} from './styles';
import {
  type BaseChoice,
  SelectScreen,
} from 'components/Funnelling/screens/SelectScreen';
import { useJob } from 'components/Funnelling/job.context';
import type { PreferredStartDate } from 'lib/sdks/jobs-management/job.type';
import type { SelectConfigChoice } from '@ts-types/category-funnelling';
import {
  QUESTION_FORMAT,
  type AnsweredQuestion,
} from 'components/JobRefinement';
import { useJobRefinementState } from 'components/JobRefinement/JobRefinement.reducer';
import { Experiment, useOptimizely } from 'lib/sdks/optimizely';
import { useRAQJobStartDate } from '@hooks/use-raq-job-startdate';
import { Card, Heading } from '@checkatrade/web-ui';
import { isExpressEnabled } from './data/express-enabled';

const RAQEntryPoint = dynamic(() => import('./components/RAQEntryPoint'));
const ExpressEntryPointCards = dynamic(
  () => import('./components/ExpressEntryPointsCards'),
);

const RAQBanner = ({
  setOpen,
  onContactTrades,
  postcode,
  searchLocation,
  categoryId,
  requestId,
}: {
  setOpen: () => void;
  onContactTrades: (
    answer?: AnsweredQuestion,
    skipSummaryScreen?: boolean,
  ) => void;
  postcode: string | undefined;
  searchLocation: string;
  categoryId: number;
  requestId: string;
}) => {
  const { state } = useJobRefinementState();
  const variant = state.form.variant;
  const { setJobField } = useJob();

  const { decide, loading: optimizelyLoading } = useOptimizely();

  const { title, options } = useRAQJobStartDate();

  const jobRefinementEnabled = useMemo(() => {
    if (optimizelyLoading) return false;
    const decision = decide(Experiment.LEAD_ENRICHMENT_FORM);
    return (
      decision?.enabled &&
      ['variant_a', 'variant_b', 'variant_c'].includes(
        decision?.variationKey ?? '',
      )
    );
  }, [optimizelyLoading, decide]);

  const expressRaqEntryPointEnabled = useMemo(() => {
    if (optimizelyLoading) return false;

    if (isExpressEnabled(postcode, categoryId)) {
      const decision = decide(Experiment.MD_790_EXPRESS_SEARCH_RAQ_CARD);
      return (
        decision?.enabled && decision?.variationKey === 'with-express-card'
      );
    }

    return false;
  }, [optimizelyLoading, decide, postcode, categoryId]);

  const raqEntryPointEnabled = useMemo(() => {
    if (optimizelyLoading) return false;
    const decision = decide(Experiment.RAQ_ENTRY_POINT);
    return decision?.enabled && decision?.variationKey === 'new_raq_banner';
  }, [optimizelyLoading, decide]);

  const description =
    "Give us the details of your job and we'll send it to specialist trades for you.";

  const openRAQForm = () => {
    setOpen();
  };

  const question = state.form.questions?.find(
    q => q.format === QUESTION_FORMAT.JOB_PREFERRED_STARTS,
  );

  const openRaqEntryPointForm = (answer?: {
    value: string;
    label: string;
    title: string;
  }) => {
    onContactTrades(
      question && answer?.value
        ? {
            questionId: question.questionId,
            title: question.title,
            format: question.format,
            answers: [{ key: answer.value, value: answer.label }],
          }
        : undefined,
      raqEntryPointEnabled,
    );
  };

  // If JobRefinement experiment is dropped - delete this
  const openLeadEnrichmentForm = (answer?: {
    value: string;
    label: string;
    title: string;
  }) => {
    onContactTrades(
      question &&
        answer?.value &&
        !['variant_b', 'variant_c'].includes(variant ?? '')
        ? {
            questionId: question.questionId,
            title: question.title,
            format: question.format,
            answers: [{ key: answer.value, value: answer.label }],
          }
        : undefined,
    );
  };

  // If JobRefinement experiment is dropped - delete this
  // const choices = question?.options && jobRefinementEnabled
  //     ? question?.options.map(option => ({
  //       ...option,
  //       title: option.label,
  //     }))
  //     : startDateOptions.map(option => ({
  //       ...option,
  //       title: option.label,
  //     }));

  const labelToTitle = (option: {
    label: string;
    value: string | number;
  }): BaseChoice => ({
    ...option,
    title: option.label,
  });

  const handleSelect = (choice: SelectConfigChoice) => {
    setJobField('preferredStart', {
      label: choice.title,
      option: choice.value,
      level: choice.level,
    } as PreferredStartDate);

    openRAQForm();
  };

  if (optimizelyLoading)
    return (
      <Card className="flex w-screen flex-col rounded-t-none bg-main p-4 md:w-full md:rounded-t-lg md:p-6">
        <div className="mb-3 h-6 w-1/2 animate-pulse rounded-lg bg-neutral-10 md:mb-2" />
        <div className="mb-3 h-4 w-full animate-pulse rounded-lg bg-neutral-10 md:w-1/4" />
        <div className="h-32 w-full animate-pulse rounded-lg bg-neutral-10 md:mb-2 md:h-11" />
        <div className="mb-2 hidden h-11 w-full animate-pulse rounded-lg bg-neutral-10 md:block" />
        <div className="hidden h-11 w-full animate-pulse rounded-lg bg-neutral-10 md:block" />
      </Card>
    );

  return (
    <>
      {raqEntryPointEnabled ? (
        <div>
          <RAQEntryPoint onClick={openRaqEntryPointForm} />
        </div>
      ) : jobRefinementEnabled &&
        (state.form.variant === 'variant_b' ||
          state.form.variant === 'variant_c') ? (
        <div>
          <RAQEntryPoint onClick={openLeadEnrichmentForm} />
        </div>
      ) : (
        <RAQBannerWrapper className="shadow-lg">
          <MobileView>
            <Heading
              as="h3"
              variant="headingSM"
              className="text-2xl font-bold text-secondary"
            >
              Describe your job
            </Heading>
            <BodyTextSmall>{description}</BodyTextSmall>
            <FauxButton
              onClick={
                // jobRefinementEnabled ? openLeadEnrichmentForm : openRAQForm
                openRAQForm
              }
            >
              {title}
            </FauxButton>
          </MobileView>
          <DesktopView>
            <SelectScreen
              title={title}
              choices={options.map(labelToTitle)}
              desktopView
              showSubheading
              onSelect={
                // jobRefinementEnabled ? openLeadEnrichmentForm : handleSelect
                handleSelect
              }
            />
          </DesktopView>
        </RAQBannerWrapper>
      )}

      {expressRaqEntryPointEnabled && (
        <ExpressEntryPointCards
          postcode={postcode}
          searchLocation={searchLocation}
          categoryId={categoryId}
          requestId={requestId}
        />
      )}
    </>
  );
};

export default RAQBanner;
